import type { AllSettings } from '@/contexts/SiteSettingsContext';
import { resolveAndPublishLoadingBlock } from '@/lib/branding/publishLoadingBlockConfig';
import { parsePublicLoadingConfig, setPublicUiBootstrap } from '@/lib/branding/publicUiBootstrap';
import type { PublicBrandingRaw } from '@/lib/branding/types';
import { emitBrandingRefreshed } from '@/lib/auth/sessionCoordinator';

const PUBLIC_BRANDING_CACHE_KEY = 'branding:v1';

/**
 * Sync public caches after admin save so runtime, boot splash, and F5 stay consistent.
 */
export function syncLoadingBlockCaches(settings: AllSettings): void {
  if (typeof window === 'undefined') return;

  const publicUi = parsePublicLoadingConfig({
    loading_animation_type: settings.ui.loading_animation_type,
    loader_indicator_duration_ms: settings.ui.loader_indicator_duration_ms,
    enable_animations: settings.ui.enable_animations,
  } as PublicBrandingRaw);

  setPublicUiBootstrap(publicUi);

  const raw: PublicBrandingRaw = {
    site_title: settings.branding.site_title || null,
    logo_url: settings.branding.logo_url,
    favicon_url: settings.branding.favicon_url,
    loading_image_url: settings.branding.loading_image_url,
    loading_animation_type: settings.ui.loading_animation_type,
    loader_indicator_duration_ms: settings.ui.loader_indicator_duration_ms,
    enable_animations: settings.ui.enable_animations,
    updated_at: new Date().toISOString(),
  };

  try {
    const payload = {
      version: 1 as const,
      savedAt: Date.now(),
      data: raw,
    };
    localStorage.setItem(PUBLIC_BRANDING_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }

  resolveAndPublishLoadingBlock({
    scope: 'boot',
    siteBranding: settings.branding,
    siteUi: settings.ui,
  });

  emitBrandingRefreshed('syncLoadingBlockCaches');
}
